import React, {
	useMemo,
	useEffect,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';
import { HomeNotification, NOTIFICATION_TYPES } from '../../../stores/reducers/home';
import TellyCheer from '../../../images/telly-cheer.svg';
import { loadInvitations } from '../../../stores/actions/invitations';
import {
	setSelectedMeeting, setOnboardingComplete
} from '../../../stores/actions/session';
import { setActiveTab, setRightDrawerOpen } from '../../../stores/actions/tabs';
import { LoadMeeting } from '../../../connection/meeting';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import TellyCard from '../../../images/telly-card.png';
import { SetNotificationsSeen } from '../../../connection/home';
import { GreenroomState } from '../../../stores/types/types';
import { TABS } from '../../../stores/types/tabs';

function getNotificationId(notification: HomeNotification): number | undefined {
	return notification.notification;
}

interface Props {
	open: boolean;
	onClose: Function;
}

interface NotificationGroup {
	hourStart: number,
	meeting: number,
	type: string,
	notifications: HomeNotification[]
}

export default function NotificationCenter(props: Props) {
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const notifications = useSelector((state: GreenroomState) => state.HomeReducer.notifications);
	// const { open, onClose } = props;
	const { onClose } = props;
	const [isOpen, setIsOpen] = useState<boolean>(props.open);
	const dispatch = useDispatch();

	function handleClick(e: any) {
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (path[i].className?.includes && typeof path[i].className === 'string') {
				if (path[i].className.includes('dialog-background')
					|| path[i].className.includes('dialog-container')
					|| path[i].className.includes('notification-center')) {
					keepOpen = true;
					break;
				}
			}
		}
		if (!keepOpen) onClose();
	}

	useEffect(() => {
		if (isOpen) window.addEventListener('click', handleClick);
		else window.removeEventListener('click', handleClick);

		return () => { window.removeEventListener('click', handleClick); };
	}, [isOpen]);

	useEffect(() => {
		setIsOpen(props.open);
	}, [props.open]);

	async function selectMeeting(meetingID: number, type: string) {
		if (!token) return;

		const meeting = await LoadMeeting(token, meetingID);

		dispatch(loadInvitations(token!, meetingID));
		dispatch(setSelectedMeeting(meeting));
		window.history.pushState(undefined, 'Greenroom', `/${host ? 'producer/' : ''}${meeting.meeting_id}`);
		onClose();

		dispatch(setOnboardingComplete(true));

		if (type === 'comment-basic' || type === 'comment-mention') {
			setTimeout(() => {
				dispatch(setActiveTab(TABS.CHAT));
				dispatch(setRightDrawerOpen(true));
			}, 100);
		}

		if (notifications) {
			const notificationIDs = notifications.map(getNotificationId);
			const cleanNotificationIDs = notificationIDs.filter((id: any) => !!id) as number[];
			await SetNotificationsSeen(token, cleanNotificationIDs);
		}
	}

	const parseMessage = (notification: HomeNotification) => {
		let { message } = notification.data;

		let name = notification.data.first_name;
		if (notification.data.last_name) name = `${name} ${notification.data.last_name}`;

		message = message.replace('%%notification_sender%%', name);
		message = message.replace('%%notification_meeting%%', notification.data.meeting_name);

		if (notification.data.timestamp_data) {
			const dateString = new Date(notification.data.timestamp_data).toLocaleString('en-us', {
				weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: 'numeric',
			});
			message = message.replace('%%notification_timestamp_data%%', dateString);
		}

		return <><ReactMarkdown>{message}</ReactMarkdown></>;
	};

	const parseDate = (notification: HomeNotification) => {
		if (!notification.created) return '';

		const now = new Date().valueOf();
		const created = new Date(notification.created).valueOf();

		const secondsSinceCreated = (now - created) / 1000;
		if (secondsSinceCreated < 60) return 'just now';

		const minutesSinceCreated = Math.floor(secondsSinceCreated / 60);
		if (minutesSinceCreated < 60) return `${minutesSinceCreated}m ago`;

		const hoursSinceCreated = Math.floor(minutesSinceCreated / 60);
		if (hoursSinceCreated < 24) return `${hoursSinceCreated} hour${hoursSinceCreated === 1 ? '' : 's'} ago`;

		const daysSinceCreated = Math.floor(hoursSinceCreated / 24);
		return `${daysSinceCreated} day${daysSinceCreated === 1 ? '' : 's'} ago`;
	};

	const makeGroup = (not: HomeNotification, timeCreated: number): NotificationGroup => ({
		meeting: not.meeting,
		type: not.type,
		hourStart: timeCreated, // for grouping comments by hour
		notifications: [not],
	});

	const parseGroups = (groups: NotificationGroup[]): HomeNotification[] => {
		let returnedNotifications: HomeNotification[] = [];

		for (let i = 0; i < groups.length; i += 1) {
			const group = groups[i];
			if (group.type === NOTIFICATION_TYPES.COMMENT_BASIC) {
				const notificationToReturn = group.notifications[0];

				const userToCheck = group.notifications[0].sending_user;
				const allNotificationsAreFromOneUser = group.notifications.every(
					(not: HomeNotification) => not.sending_user === userToCheck,
				);

				if (allNotificationsAreFromOneUser) {
					notificationToReturn.data.message = `**%%notification_sender%%** sent you ${group.notifications.length} message${group.notifications.length > 1 ? 's' : ''} in **%%notification_meeting%%**`;
				} else {
					notificationToReturn.data.message = `You received ${group.notifications.length} chat message${group.notifications.length > 1 ? 's' : ''} in **%%notification_meeting%%**`;
				}

				if (!notificationToReturn.data.picture) {
					const anyNotificationInGroupWithPicture = group.notifications.find(
						(not: HomeNotification) => not.data.picture,
					);
					if (anyNotificationInGroupWithPicture) {
						notificationToReturn.data.picture = anyNotificationInGroupWithPicture.data.picture;
					}
				}

				returnedNotifications.push(notificationToReturn);
			} else {
				returnedNotifications = [...returnedNotifications, ...group.notifications];
			}
		}
		return returnedNotifications;
	};

	const groupNotifications = (nots: HomeNotification[]): HomeNotification[] => {
		if (nots.length <= 1) return nots;

		const groups = [];
		let group = null;

		let i = 0;
		const len = nots.length;
		for (i; i < len; i += 1) {
			const timeCreated = new Date(nots[i].created).valueOf();
			if (group) {
				if (
					nots[i].meeting === group.meeting
					&& nots[i].type === group.type
					&& group.hourStart - timeCreated < (1000 * 60 * 60) // groups are hour gated
				) {
					// if this notification belongs in this group
					group.notifications.push(nots[i]);
				} else {
					// save grouping, start new group
					groups.push(group);
					group = makeGroup(nots[i], timeCreated);
				}
			} else {
				group = makeGroup(nots[i], timeCreated);
			}
		}

		const returnedNotifications: HomeNotification[] = parseGroups(groups);

		return returnedNotifications;
	};

	const [unseenNotifications, seenNotifications] = useMemo(() => {
		let unseen = notifications.filter(
			(note: HomeNotification) => note.has_been_seen === 0,
		);
		let seen = notifications.filter(
			(note: HomeNotification) => note.has_been_seen === 1,
		);

		unseen = groupNotifications(unseen);
		seen = groupNotifications(seen);

		return [unseen, seen];
	}, [groupNotifications, notifications]);

	const notificationBubble = (type: string) => {
		let icon = '';
		let color = '';

		switch (type) {
			case NOTIFICATION_TYPES.COMMENT_BASIC:
			case NOTIFICATION_TYPES.COMMENT_MENTION:
				icon = ICONS.CHAT_NOTIFICATION;
				color = 'blue';
				break;
			case NOTIFICATION_TYPES.INVITE_PRESENTER:
			case NOTIFICATION_TYPES.INVITE_VIEWER:
				icon = ICONS.EVENT_NOTIFICATION;
				color = 'red';
				break;
			case NOTIFICATION_TYPES.REHEARSAL_CREATE:
				icon = ICONS.REHEARSAL_NOTIFICATION;
				color = 'orange';
				break;
			case NOTIFICATION_TYPES.TECHCHECK_CREATE:
				icon = ICONS.TECH_CHECK_NOTIFICATION;
				color = 'gray';
				break;
			case NOTIFICATION_TYPES.DOWNLOAD_READY:
				icon = ICONS.RECORD;
				color = 'red';
				break;
			default:
				icon = ICONS.TELLY_NOTIFICATION;
				color = 'green';
				break;
		}

		return <div className={`home-notification-icon-bubble ${color}`}>
			<Icon name={icon} color={COLORS.WHITE} size={10} />
		</div>;
	};

	return (isOpen ? <>
		<div className="home-notification-background"></div>
		<div className="home-notification-center">
			<div className={classNames('home-notification-body')}>
				<div className="home-notification-header-container">
					<div className="home-notification-header"><span>Notifications</span></div>
				</div>
				{unseenNotifications.map(
					(notification: HomeNotification, index: number) => (
						<div key={index} onClick={() => selectMeeting(
							notification.meeting,
							notification.type,
						)}>
							<div className="notification-row flex">
								<div className="home-notification-image">
									{notification.data.picture
										&& <img
											className="home-notification-avatar circle"
											alt="avatar"
											src={notification.data.picture}
										/>}
									{notification.data.wallpaper
										&& <img
											className="home-notification-avatar square"
											alt="avatar"
											src={notification.data.wallpaper}
										/>}
									{!notification.data.picture && !notification.data.wallpaper
										&& <img
											className="home-notification-avatar square telly"
											alt="avatar"
											src={TellyCard}
										/>}
									{notificationBubble(notification.type)}
								</div>
								<div className="home-notification-right">
									<div className="home-notification-message">
										{parseMessage(notification)}
									</div>
									<div className="home-notification-timing">
										{parseDate(notification)}
									</div>
								</div>
							</div>
						</div>
					),
				)}

				<div className="telly-container">
					<div>You're up to date</div>
					<div><img className="telly-cheer" alt={'Telly Cheering'} src={TellyCheer} /></div>
				</div>

				{seenNotifications.map(
					(notification: HomeNotification, index: number) => (
						<div
							key={index}
							onClick={() => selectMeeting(notification.meeting, notification.type)}
						>
							<div className="notification-row flex">
								<div className="home-notification-image">
									{notification.data.picture
										&& <img
											className="home-notification-avatar circle"
											alt="avatar" src={notification.data.picture}
										/>}
									{notification.data.wallpaper
										&& <img
											className="home-notification-avatar square"
											alt="avatar" src={notification.data.wallpaper}
										/>}
									{!notification.data.picture && !notification.data.wallpaper
										&& <img
											className="home-notification-avatar square telly"
											alt="avatar"
											src={TellyCard}
										/>}
									{notificationBubble(notification.type)}
								</div>
								<div className="home-notification-right">
									<div className="home-notification-message">
										{parseMessage(notification)}
									</div>
									<div className="home-notification-timing">
										{parseDate(notification)}
									</div>
								</div>
							</div>
						</div>
					),
				)}
			</div>
		</div>
	</> : <></>);
}
